import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { TagChip } from '../../../components/entity-detail/TagChip.js';
import { MessageOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityHeader } from '../../../components/entity-detail/EntityHeader.js';
import { useDeleteContactMutation } from '../hooks/useContactMutations.js';
import type { ContactItem } from '../api/contacts.client.js';

export function ContactHeader({
  contact,
  onChat,
  onEdit,
  onHistory,
}: {
  contact: ContactItem;
  onChat: () => void;
  onEdit: () => void;
  onHistory: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const remove = useDeleteContactMutation();
  return (
    <EntityHeader
      backTo="/contacts"
      backLabel={t('crm.features.features.contacts.pages.contactdetailpage.к_контактам')}
      title={contact.fullName}
      subtitle={[contact.email || contact.phone, contact.companyRef?.name ?? contact.company]
        .filter((part) => part && part.trim() !== contact.fullName.trim())
        .join(' · ')}
      chips={contact.tags.map((tag) => (
        <TagChip key={tag} value={tag} />
      ))}
      primary={{
        key: 'chat',
        label: t('crm.entity_detail.open_chat'),
        icon: <MessageOutlined aria-hidden />,
        testId: 'contact-open-chat',
        onClick: onChat,
      }}
      secondary={[
        ...(contact.phone
          ? [
              {
                key: 'call',
                label: t('crm.entity_detail.call'),
                href: `tel:${contact.phone}`,
                onClick: () => {},
              },
            ]
          : []),
        ...(contact.email
          ? [
              {
                key: 'write',
                label: t('crm.entity_detail.write'),
                href: `mailto:${contact.email}`,
                onClick: () => {},
              },
            ]
          : []),
        { key: 'edit', label: t('crm.entity_detail.edit'), onClick: onEdit },
      ]}
      overflow={[
        { key: 'history', label: t('crm.entity_detail.history'), onClick: onHistory },
        {
          key: 'delete',
          label: t('crm.entity_detail.delete_contact'),
          danger: true,
          disabled: remove.isPending,
          confirm: {
            title: t('crm.features.features.contacts.pages.contactdetailpage.удалить_контакт_2'),
            description: t(
              'crm.features.features.contacts.pages.contactdetailpage.сделки_задачи_и_заметки_контакта_останутся',
            ),
          },
          onClick: () => remove.mutateAsync(contact.id).then(() => navigate('/contacts')),
        },
      ]}
    />
  );
}
